import {type ReactNode, useCallback, useMemo, useRef, useState} from 'react'
import {BannerStackActionsContext, type BannerStackActionsContextValue} from './banner-stack-actions-context'
import {InternalBannerStackDataContext, type BannerData, type BannerInput} from './internal-banner-stack-data-context'
import {
  InternalBannerRegistryContext,
  type InternalBannerRegistryContextValue,
} from './internal-banner-registry-context'

export interface BannerStackProviderProps {
  children: ReactNode
}

export function BannerStackProvider({children}: BannerStackProviderProps) {
  const [banners, setBanners] = useState<BannerData[]>([])

  // Maps banner id -> its rendered root element (populated by `BannerStackOutlet` via
  // each `<Banner>`'s ref), so `focusBanner` can move focus into a specific banner.
  const bannerElsRef = useRef(new Map<string, HTMLElement>())

  const registerBannerEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      bannerElsRef.current.set(id, el)
    } else {
      bannerElsRef.current.delete(id)
    }
  }, [])

  const addBanner = useCallback((banner: BannerInput) => {
    const id = crypto.randomUUID()
    const data: BannerData = {...banner, id}

    setBanners(prev => [...prev, data])

    return id
  }, [])

  const updateBanner = useCallback((id: string, banner: Partial<BannerInput>) => {
    setBanners(prev => prev.map(existing => (existing.id === id ? {...existing, ...banner} : existing)))
  }, [])

  const dismissBanner = useCallback((id: string) => {
    setBanners(prev => prev.filter(banner => banner.id !== id))
  }, [])

  const dismissAllBanners = useCallback(() => {
    setBanners([])
  }, [])

  const focusBanner = useCallback((id: string) => {
    bannerElsRef.current.get(id)?.focus()
  }, [])

  const actionsValue = useMemo<BannerStackActionsContextValue>(
    () => ({addBanner, updateBanner, dismissBanner, dismissAllBanners, focusBanner}),
    [addBanner, updateBanner, dismissBanner, dismissAllBanners, focusBanner],
  )

  const dataValue = useMemo(() => ({banners}), [banners])

  const registryValue = useMemo<InternalBannerRegistryContextValue>(() => ({registerBannerEl}), [registerBannerEl])

  return (
    <BannerStackActionsContext value={actionsValue}>
      <InternalBannerRegistryContext value={registryValue}>
        <InternalBannerStackDataContext value={dataValue}>{children}</InternalBannerStackDataContext>
      </InternalBannerRegistryContext>
    </BannerStackActionsContext>
  )
}

BannerStackProvider.displayName = 'BannerStackProvider'
